import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import ReplayIcon from '@mui/icons-material/Replay';
import { Box, Button, Paper } from '@mui/material';
import PropTypes from 'prop-types';
import React from 'react';

import { TypographyApp } from '../custom/CustomComponents';

/**
 * Componente amigable y moderno para pantallas de error o desconexión.
 * Incorpora la paleta de colores de la aplicación y una guía clara de "Prueba esto".
 */
const EstadoErrorApp = ({
  titulo = 'Ha ocurrido un error al cargar los datos',
  subtitulo = 'No te preocupes, esto suele ser temporal. Prueba esto:',
  sugerencias = [
    {
      titulo: 'Reintentar la carga',
      descripcion: 'Vuelve a solicitar la información pulsando el botón "Reintentar ahora".',
    },
    {
      titulo: 'Comprobar tu conexión',
      descripcion: 'Asegúrate de que tu dispositivo cuente con una conexión a Internet activa y estable.',
    },
    {
      titulo: 'Recargar la página',
      descripcion: 'Actualiza la ventana del navegador o vuelve a intentarlo pasados unos minutos.',
    },
  ],
  onReintentar = null,
  onRecargar = () => window.location.reload(),
}) => {
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '60vh',
        px: 2,
        py: 4,
      }}
    >
      <Paper
        elevation={0}
        sx={{
          maxWidth: 560,
          width: '100%',
          p: { xs: 3, sm: 4.5 },
          borderRadius: 3,
          backgroundColor: '#ffffff',
          border: '1px solid #e2e8f0',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
          textAlign: 'center',
        }}
      >
        {/* Icono de advertencia en insignia circular */}
        <Box
          sx={{
            width: 60,
            height: 60,
            borderRadius: '50%',
            backgroundColor: 'rgba(15, 118, 110, 0.08)',
            color: 'primary.main',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mx: 'auto',
            mb: 2.5,
            border: '1px solid rgba(15, 118, 110, 0.18)',
          }}
        >
          <ErrorOutlineIcon sx={{ fontSize: 34 }} />
        </Box>

        {/* Título amigable */}
        <TypographyApp
          variant="h6"
          sx={{
            fontWeight: 700,
            color: 'text.primary',
            fontSize: { xs: '1.15rem', sm: '1.30rem' },
            mb: 1,
            lineHeight: 1.3,
          }}
        >
          {titulo}
        </TypographyApp>

        {/* Subtítulo de guía */}
        <TypographyApp
          variant="body2"
          sx={{
            color: 'text.secondary',
            fontSize: '0.90rem',
            mb: 3,
          }}
        >
          {subtitulo}
        </TypographyApp>

        {/* Caja de sugerencias ("Prueba esto") */}
        <Box
          sx={{
            backgroundColor: '#f8fafc',
            borderRadius: 2.5,
            border: '1px solid #e2e8f0',
            p: 2.5,
            mb: 3.5,
            textAlign: 'left',
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {sugerencias.map((item, index) => {
              const itemTitulo = typeof item === 'string' ? item : item.titulo;
              const itemDesc = typeof item === 'string' ? '' : item.descripcion;

              return (
                <Box key={index} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                  {/* Número de paso con color corporativo */}
                  <Box
                    sx={{
                      minWidth: 24,
                      height: 24,
                      borderRadius: '50%',
                      backgroundColor: 'primary.main',
                      color: '#ffffff',
                      fontSize: '0.75rem',
                      fontWeight: 700,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      mt: '1px',
                    }}
                  >
                    {index + 1}
                  </Box>
                  <Box>
                    <TypographyApp
                      sx={{
                        fontSize: '0.85rem',
                        fontWeight: 600,
                        color: 'text.primary',
                        lineHeight: 1.3,
                      }}
                    >
                      {itemTitulo}
                    </TypographyApp>
                    {itemDesc && (
                      <TypographyApp
                        sx={{
                          fontSize: '0.80rem',
                          color: 'text.secondary',
                          mt: 0.3,
                          lineHeight: 1.4,
                        }}
                      >
                        {itemDesc}
                      </TypographyApp>
                    )}
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>

        {/* Botones de acción */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 1.5,
            flexWrap: 'wrap',
          }}
        >
          {onReintentar && (
            <Button
              variant="contained"
              onClick={onReintentar}
              startIcon={<RefreshIcon sx={{ fontSize: '1.1rem' }} />}
              sx={{
                backgroundColor: 'primary.main',
                color: '#ffffff',
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.85rem',
                borderRadius: '8px',
                px: 2.5,
                py: 0.9,
                boxShadow: '0 2px 4px rgba(15, 118, 110, 0.2)',
                '&:hover': {
                  backgroundColor: 'primary.dark',
                  boxShadow: '0 4px 8px rgba(15, 118, 110, 0.3)',
                },
              }}
            >
              Reintentar ahora
            </Button>
          )}

          {onRecargar && (
            <Button
              variant="outlined"
              onClick={onRecargar}
              startIcon={<ReplayIcon sx={{ fontSize: '1.1rem' }} />}
              sx={{
                borderColor: '#cbd5e1',
                color: 'text.primary',
                textTransform: 'none',
                fontWeight: 500,
                fontSize: '0.85rem',
                borderRadius: '8px',
                px: 2.5,
                py: 0.9,
                '&:hover': {
                  borderColor: '#94a3b8',
                  backgroundColor: '#f8fafc',
                },
              }}
            >
              Recargar página
            </Button>
          )}
        </Box>
      </Paper>
    </Box>
  );
};

EstadoErrorApp.propTypes = {
  titulo: PropTypes.string,
  subtitulo: PropTypes.string,
  sugerencias: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        titulo: PropTypes.string.isRequired,
        descripcion: PropTypes.string,
      }),
    ])
  ),
  onReintentar: PropTypes.func,
  onRecargar: PropTypes.func,
};

export default EstadoErrorApp;
